import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { i18n, L10N_KEYS } from '@/l10n/i18n';
import type { L10n, L10nKey } from '@/l10n/types';

export { setL10nLanguage } from '@/l10n/i18n';

/**
 * Три двери к переводам — ровно те же, что в
 * `lib/src/utils/extensions/build_context_extension.dart`:
 *
 *  - [useL10n]     — `context.l10n` внутри виджета;
 *  - [getL10n]     — `AppLocalizations.current` вне дерева виджетов
 *                    (контроллеры, менеджеры, провайдеры);
 *  - [tryGetL10n]  — то же, но никогда не бросает.
 *
 * Объект [L10n] собирается из списка ключей основной локали, а не
 * копированием строк: у каждого поля свой геттер, который спрашивает
 * перевод у i18next в момент чтения. Поэтому значение всегда соответствует
 * текущему языку — как геттер сгенерированного класса `AppLocalizations`.
 */

/**
 * Сообщение assert-а из `l10n.dart`, адаптированное под веб: там оно
 * напоминало добавить делегат, тут — вызвать [initI18n] при старте.
 */
const NOT_INITIALIZED_MESSAGE =
  'No instance of L10n was loaded. Call initI18n() before accessing getL10n().';

function buildL10n(translate: (key: L10nKey) => string): L10n {
  const l10n = {} as Record<L10nKey, string>;
  for (const key of L10N_KEYS) {
    Object.defineProperty(l10n, key, {
      get: (): string => translate(key),
      enumerable: true,
    });
  }
  return Object.freeze(l10n);
}

/** `context.l10n` — перерисовывает потребителя при смене языка. */
export function useL10n(): L10n {
  const { t, i18n: instance } = useTranslation();
  // Пересобираем объект при смене языка, чтобы у него менялась ссылка:
  // мемоизированные компоненты должны увидеть новые переводы.
  // `instance.language` здесь не лишняя зависимость: она и есть сигнал
  // пересобрать объект при смене языка, иначе мемоизированные потребители
  // не увидят новых переводов.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useMemo(() => buildL10n((key) => t(key)), [t, instance.language]);
}

/**
 * `AppLocalizations.current` — доступ к переводам вне React.
 *
 * Бросает, если переводы ещё не загружены: в Dart на этом же месте падал
 * assert, и молча отдавать ключи вместо текста нельзя — это скрыло бы
 * ошибку порядка инициализации.
 */
export function getL10n(): L10n {
  if (!i18n.isInitialized) {
    throw new Error(NOT_INITIALIZED_MESSAGE);
  }
  return buildL10n((key) => i18n.t(key));
}

/**
 * `tryGetL10n()` — `null` вместо исключения.
 *
 * `null` здесь не проглоченная ошибка, а контракт: вызывающий (например
 * менеджер снекбаров) сам решает, что показать без переводов. Так же
 * устроена Dart-версия.
 */
export function tryGetL10n(): L10n | null {
  try {
    return getL10n();
  } catch {
    return null;
  }
}
